import { countries, currentMsMarket } from "@ms/us/src/@ms/gatsby-site/config/navigation";
import { graphql, useStaticQuery } from "gatsby";
import { useMemo } from "react";

import {
  NavigationDataProps,
  NavigationStaticText,
  SiteNavData,
  SiteNavQueryData,
  getLocalizedNavData,
} from "@ms/gatsby-theme";

const baseUrl = "https://www.manscaped.com";

const NAVIGATION_QUERY = graphql`
  query NavigationQuery {
    strapiSite(Instance: { eq: "US" }) {
      id
      Instance
      navigation {
        localizations {
          data {
            attributes {
              locale
              promoBanner {
                description
                badge
              }
            }
          }
        }
        id
        locale
        promoBanner {
          description {
            data {
              description
            }
          }
          badge
        }
        categories {
          ...StrapiNavigationCategory
        }
      }
    }
    shopAllCategory: strapiCategory(slug: { eq: "shop-all" }, locale: { eq: "en" }) {
      ...StrapiNavigationCategory
    }
    strapiBlogAsset {
      featuredImage {
        title
        subtitle
        cta {
          label
          link
          ariaLabel
        }
        image {
          cloudinaryMedia {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
`;

/**
 * Use Nav Data
 *
 * This hook takes in the Navigation from Strapi for the given instance and localizes the content -
 * also to allow all navigation components to live in our gatsby-theme component library so we can use
 * the same nav in multiple places
 *
 * @returns {NavigationDataProps} navData
 */
export const useBlogNavData = (): NavigationDataProps => {
  const { strapiSite, shopAllCategory, strapiBlogAsset } = useStaticQuery(NAVIGATION_QUERY);
  const siteNavData: SiteNavQueryData = strapiSite.navigation;

  const currentLanguage = "en";

  const navigationStaticText: NavigationStaticText = useMemo(
    () => ({
      new: "New",
      bestSeller: "Best Seller",
      premium: "Premium",
      trending: "Trending",
      freeGift: "Free Gift",
      exclusive: "Exclusive",
      limitedEdition: "Limited Edition",
      specialEdition: "Special Edition",
      shopAll: "Shop All",
      shopNow: "Shop Now",
      back: "Back",
    }),
    [],
  );

  const localizedNavData: SiteNavData = useMemo(
    () =>
      getLocalizedNavData(
        siteNavData,
        shopAllCategory,
        currentLanguage,
        currentMsMarket,
        true,
        navigationStaticText,
        strapiBlogAsset,
      ),
    [navigationStaticText, shopAllCategory, siteNavData],
  );

  return {
    cart: {
      itemsInCart: 0,
      to: `${baseUrl}/cart`,
    },
    isAuthenticated: false,
    isBlog: true,
    countries: countries,
    currentMsMarket: currentMsMarket,
    languages: [currentLanguage],
    currentLanguage: currentLanguage,
    originalPath: "",
    siteNavData: localizedNavData,
    blogLink: null,
    homeUrl: `${baseUrl}/`,
    additionalLinks: [
      {
        to: `${baseUrl}/pages/peak-hygiene-plan`,
        label: "Peak Hygiene Plan",
      },
      {
        to: `${baseUrl}/pages/we-save-balls`,
        label: "Our Cause",
      },
    ],
    accountLinks: [],
    accountAuthenticatedLink: {
      to: `${baseUrl}/account/edit`,
      label: "My Account",
      mobileLabel: "My Account",
    },
    productLabel: "Products",
    accountLabel: "My Account",
    loginLink: {
      to: `${baseUrl}/login`,
      label: "Sign In",
      mobileLabel: "Login to My Account",
    },
    signOut: { action: () => null, label: "Sign Out" },
  };
};
